var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useCallback, useEffect, useReducer, useRef } from 'react';
import cropImage from './cropImage';
export var CroppedImageStatus;
(function (CroppedImageStatus) {
    CroppedImageStatus["Initial"] = "Initial";
    CroppedImageStatus["Loading"] = "Loading";
    CroppedImageStatus["Success"] = "Success";
    CroppedImageStatus["Error"] = "Error";
})(CroppedImageStatus || (CroppedImageStatus = {}));
var CropImageActionType;
(function (CropImageActionType) {
    CropImageActionType["InProgress"] = "InProgress";
    CropImageActionType["Complete"] = "Complete";
    CropImageActionType["Errored"] = "Errored";
    CropImageActionType["Updated"] = "Updated";
    CropImageActionType["Reset"] = "Reset";
})(CropImageActionType || (CropImageActionType = {}));
const buildCroppedImageReducerInitialState = (placeholderImageSrc) => ({
    imageSrc: placeholderImageSrc !== null && placeholderImageSrc !== void 0 ? placeholderImageSrc : null,
    status: CroppedImageStatus.Initial
});
const reducer = (state, action) => {
    switch (action.type) {
        case CropImageActionType.InProgress:
            return Object.assign(Object.assign({}, state), { status: CroppedImageStatus.Loading });
        case CropImageActionType.Updated:
            return Object.assign(Object.assign({}, state), { imageSrc: action.imageSrc });
        case CropImageActionType.Complete:
            return Object.assign(Object.assign({}, state), { status: CroppedImageStatus.Success, imageSrc: action.imageSrc });
        case CropImageActionType.Errored:
            return Object.assign(Object.assign({}, state), { status: CroppedImageStatus.Error, error: action.error });
        case CropImageActionType.Reset:
            return buildCroppedImageReducerInitialState(action.placeholderImageSrc);
        default:
            return state;
    }
};
/**
 * Given a provided imageSrc, it is scaled and cropped and returned as base64
 * @param originalImageSrc The address or URL of the a media resource that is to be considered.
 * @param cropDetails The desired cropDetails.
 * @param options Various optional params, from placeholder image to canvas context interactions.
 */
const useCroppedImage = (originalImageSrc, cropDetails, options = {}) => {
    const _a = options !== null && options !== void 0 ? options : {}, { placeholderImageSrc } = _a, otherOptions = __rest(_a, ["placeholderImageSrc"]);
    const [{ imageSrc, status, error }, dispatch] = useReducer(reducer, buildCroppedImageReducerInitialState(placeholderImageSrc));
    const cropAndSetImageSrc = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            dispatch({ type: CropImageActionType.InProgress });
            const croppedImageSrc = yield cropImage(originalImageSrc, cropDetails, otherOptions);
            dispatch({ type: CropImageActionType.Complete, imageSrc: croppedImageSrc });
        }
        catch (error) {
            console.error(`Error while cropping image: ${originalImageSrc}: `, error);
            dispatch({ type: CropImageActionType.Errored, error });
        }
    }), [originalImageSrc, cropDetails, otherOptions]);
    useEffect(() => {
        if (placeholderImageSrc && placeholderImageSrc !== imageSrc && status !== CroppedImageStatus.Success) {
            dispatch({ type: CropImageActionType.Updated, imageSrc: placeholderImageSrc });
        }
    }, [placeholderImageSrc, imageSrc, status]);
    useEffect(() => {
        if (originalImageSrc && cropDetails && status === CroppedImageStatus.Initial) {
            cropAndSetImageSrc();
        }
    }, [originalImageSrc, cropDetails, status, cropAndSetImageSrc]);
    const previousOriginalImageSrc = useRef(null);
    useEffect(() => {
        if (previousOriginalImageSrc.current !== originalImageSrc && status !== CroppedImageStatus.Initial) {
            dispatch({ type: CropImageActionType.Reset, placeholderImageSrc });
        }
        previousOriginalImageSrc.current = originalImageSrc;
    }, [originalImageSrc, placeholderImageSrc, status]);
    return { imageSrc, status, error, retry: cropAndSetImageSrc };
};
export default useCroppedImage;
//# sourceMappingURL=index.js.map